import { useEffect, useRef } from 'react';
import env from '../../../env';
import TrackEventsUser from '../../../track/trackEventsUser';
import { MostraSeuAprovaModal } from '.';
import { iMostraSeuAprovaContent } from './data';
import { consumePendingTrigger } from './pendingTrigger';
import { useMostraSeuAprovaStore } from './store';
import { useCampaignState } from './useCampaignState';
import { useMostraSeuAprovaTrigger } from './useMostraSeuAprovaTrigger';

/**
 * Instância única do modal da campanha #mostraseuaprova, montada no topo da
 * aplicação.
 *
 * Fica aqui, e não dentro de uma página, porque os gatilhos partem de telas
 * espalhadas (fim de aula, correção de redação, simulado…) e o modal precisa
 * sobreviver à navegação que costuma vir logo depois. As páginas só chamam
 * `useMostraSeuAprovaTrigger().fire(key)`.
 *
 * Nada abre sozinho: a apresentação também depende de um gatilho — é o primeiro
 * que o aluno aciona que traz o vídeo, sem gastar nenhum dos três modais.
 */
export default function GlobalMostraSeuAprova() {
  const { content, close } = useMostraSeuAprovaStore();
  const { isLoading, state } = useCampaignState();
  const { fire } = useMostraSeuAprovaTrigger();

  // Gatilho deixado por um fluxo que recarregou a página (ver `pendingTrigger`).
  // Espera o estado da campanha chegar, senão `fire` desistiria antes de saber
  // se o aluno é elegível. Consome mesmo quando não há nada a exibir: o pendente
  // vale para este retorno, não para a sessão inteira.
  const pendingHandled = useRef(false);

  useEffect(() => {
    if (isLoading || pendingHandled.current) return;

    pendingHandled.current = true;

    const triggerKey = consumePendingTrigger();

    if (triggerKey) fire(triggerKey);
  }, [isLoading, fire]);

  if (!content) return null;

  const handleCtaClick = (data: iMostraSeuAprovaContent) => {
    // A inscrição acontece fora da plataforma e o status leva algum tempo para
    // refletir na base externa; o evento é o que permite acompanhar a conversão
    // enquanto isso.
    TrackEventsUser(`mostra_seu_aprova_cta_${data.key}`, 'morpheus');
  };

  return (
    <MostraSeuAprovaModal
      content={content}
      isOpen
      onClose={close}
      onCtaClick={handleCtaClick}
      // O servidor escolhe o destino pela situação do aluno; a env fica como
      // rede de segurança para quando o estado ainda não chegou.
      ctaUrl={state?.ctaUrl || env.REACT_APP_MSA_CTA_URL || undefined}
    />
  );
}
